import { get } from "lodash";
import {
  fork,
  takeLatest,
  call,
  put,
  cancel,
  select
} from "redux-saga/effects";
import pusher from "../store/Pusher.js";

import { STARTUP } from "../actions/actionConstants.js";
import { CONFIG_FILE } from "../actions/actionConstants.js";
import { CONTENT_TYPE } from "../../constants/api.js";

import { propertiesToJSON } from "../../utils/configUtils.js";

import { apiError } from "../reducers/api/apiActions.js";
import configActions from "../reducers/config/configActions.js";
import localeActions from "../reducers/locale/localeActions.js";
import notificationActions from "../reducers/notifications/notificationActions.js";

import { endpointsSaga } from "./endpointSagas.js";
import { getLowercaseContentType } from "../../utils/responseUtils.js";
import { authFlowSaga, authStartupSaga } from "./authenticationSagas.js";

// -- STATE SELECTORS -- //

const getLocale = state => get(state, "locale.locale", null);

// -- SAGAS -- //

export function* startupSaga(action) {
  // console.log("startupSaga", action);

  // load the base things we require
  try {
    yield call(configSaga, action);

    yield call(localeSaga, action);

    yield call(systemNotificationsSaga, action);

    yield call(endpointsSaga, action);

    // NOT 100% SURE WHERE THIS SHOULD GO YET
    pusher.setupPusher(false);
  } catch (err) {
    console.warn("startupSaga :: error ::", err);
    cancel();
  }

  // we have what we need start the auth flow
  try {
    yield call(authStartupSaga);
    yield fork(authFlowSaga);
  } catch (err) {
    console.warn("startupSaga :: authFlowSaga :: error ::", err);
    cancel();
  }
}

export function* configSaga(action) {
  // console.log("configSaga", action);

  yield put(configActions.requestConfig(CONFIG_FILE));

  const { response, error } = yield doFetch(`/assets/data/${CONFIG_FILE}`, {});

  if (response) {
    const contentType = getLowercaseContentType(response);

    switch (contentType) {
      case CONTENT_TYPE.PROPERTIES:
      case CONTENT_TYPE.OCTET_STREAM:
        const result = yield response.text();
        yield put(configActions.receiveConfig(propertiesToJSON(result)));
        break;

      // for other request type throw the response
      default:
        console.error("FATAL ERROR :: Unknown ContentType");
        const genError = new Response("Unknown ContentType", {
          status: 418,
          statusText: `Unknown ContentType (${contentType})`
        });
        yield put(apiError({ error: genError, action }, true));
        yield put(configActions.failureConfig(response));
    }
  } else {
    // send entity failure for reducers
    yield put(apiError({ error, action }, true));

    // error for reducer
    yield put(configActions.failureConfig(error));
  }
}

export function* localeSaga(action) {
  // console.log("localeSaga", action);

  let locale = get(action, "payload.locale", null);
  if (!locale) {
    locale = yield select(getLocale);
  }

  yield put(localeActions.requestLocaleCopy(locale));

  const { response, error } = yield doFetch(
    `/assets/data/locale/${locale}.${__VERSION__}.json`,
    {}
  );

  if (response) {
    if (response.ok !== true) {
      dispatch(localeActions.failureLocaleCopy(response.status));
      throw Error(response.status);
    }

    const contentType = getLowercaseContentType(response);

    switch (contentType) {
      case CONTENT_TYPE.JSON:
      case CONTENT_TYPE.JSON_CHARSET:
        const result = yield response.json();
        yield put(localeActions.receiveLocaleCopy(locale, result));
        break;

      // for other request type throw the response
      default:
        console.error("FATAL ERROR :: Unknown ContentType");
        const genError = new Response("Unknown ContentType", {
          status: 418,
          statusText: `Unknown ContentType (${contentType})`
        });
        yield put(apiError({ error: genError, action }, true));
        yield put(localeActions.failureConfig(response));
    }
  } else {
    // send entity failure for reducers
    yield put(apiError({ error, action }, true));

    // error for reducer
    yield put(localeActions.failureConfig(error));
  }
}

export function* systemNotificationsSaga(action) {
  // console.log("systemNotificationsSaga", action);

  yield put(notificationActions.requestSystemNotifications());
  const { response, error } = yield doFetch(
    `/excluded-from-sync/system-notifications.json`,
    {}
  );

  if (response) {
    if (response.ok !== true) {
      dispatch(notificationActions.failureSystemNotifications(response.status));
      throw Error(response.status);
    }

    const contentType = getLowercaseContentType(response);

    switch (contentType) {
      case CONTENT_TYPE.JSON:
      case CONTENT_TYPE.JSON_CHARSET:
        const result = yield response.json();
        yield put(notificationActions.receiveSystemNotifications(result));
        break;

      // for other request type throw the response
      default:
        yield put(notificationActions.failureSystemNotifications(response));
    }
  } else {
    yield put(notificationActions.failureSystemNotifications(error));
  }
}

// -- UTILS -- //

function doFetch(fullUrl, options) {
  return fetch(fullUrl, options)
    .then(response => {
      if (!response.ok) {
        throw response;
      }
      return response;
    })
    .then(response => ({ response }))
    .catch(error => {
      console.log("startupSagas :: doFetch :: error ::", error);
      if (error.status) {
        return { error };
      } else {
        const genError = new Response("Generated 520: Unknown Error", {
          status: 520,
          statusText: "Unknown Error"
        });
        return { error: genError };
      }
    });
}

// -- WATCHERS -- //

export function* watchStartupSagas() {
  yield takeLatest(STARTUP.GET, startupSaga);
}
